/** Wire shape of one feature's properties from GET /vehicles/md. */
export interface VanFeatureProperties {
    id: string;
    display_name: string;
    registration?: string | null;
    /** Derived server-side at query time from position facts + freshness thresholds. */
    state: 'moving' | 'stationary' | 'parked' | 'stale' | 'offline' | 'unknown';
    /** Degrees clockwise from north; null when the provider sent no course. */
    heading?: number | null;
    speed_kph?: number | null;
    /** ISO-8601 UTC. */
    position_time: string;
    /** Mapped Orbit user → provider operative → raw source name fallback, or null. */
    operative_name?: string | null;
    is_stale: boolean;
}

const STATE_LABELS: Record<VanFeatureProperties['state'], string> = {
    moving: 'Moving',
    stationary: 'Stationary',
    parked: 'Parked',
    stale: 'Stale',
    offline: 'Offline',
    unknown: 'Unknown',
};

export function formatVanState(state: VanFeatureProperties['state']): string {
    return STATE_LABELS[state];
}

// Plates are matched ignoring case and internal spacing ("SV24 ONE" vs "sv24one") because
// the provider's free-text object name and its licence-plate field are keyed by different
// people. Separator characters left dangling once the plate is gone (" - ", "()", "/") are
// trimmed from the ends only, so the make/model wording itself is untouched.
const EDGE_SEPARATORS = /^[\s\-–—|/,:;()[\]]+|[\s\-–—|/,:;()[\]]+$/g;

/**
 * The details-panel title: the vehicle's display name with its registration removed. The
 * provider's object name is free text that fleets typically key as make/model plus plate;
 * the panel shows the plate in its own Registration field, so the title keeps only the
 * make/model part. Null when nothing but the plate was there, so the panel can omit the
 * title rather than repeat the plate.
 */
export function vanPanelTitle(van: Pick<VanFeatureProperties, 'display_name' | 'registration'>): string | null {
    const name = van.display_name.trim();
    const plate = van.registration?.trim();
    if (!plate) return name || null;

    const plateChars = plate.replace(/\s+/g, '');
    if (plateChars === '') return name || null;

    // Find the plate inside the name allowing arbitrary whitespace between its characters.
    const pattern = new RegExp(plateChars.split('').map(escapeRegExp).join('\\s*'), 'gi');
    const stripped = name
        .replace(pattern, ' ')
        .replace(/\s{2,}/g, ' ')
        .replace(EDGE_SEPARATORS, '');
    return stripped === '' ? null : stripped;
}

function escapeRegExp(char: string): string {
    return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
